import { useCallback, useEffect, useRef, useState } from "react";
export function canOfferNearbyFirst(permission) {
  return permission === "granted" || permission === "prompt" || permission === "unknown";
}
export function useNearbyFirst({
  storageKey = "showNearbyFavouritesFirst",
  useLocalStorage
}) {
  const [showNearbyFirst, setShowNearbyFirst,, preferenceHydrated] = useLocalStorage(storageKey, false);
  const [geolocationPermission, setGeolocationPermission] = useState("checking");
  const [userLocation, setUserLocation] = useState(null);
  const restoredPreference = useRef(false);
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeolocationPermission("unsupported");
      return undefined;
    }
    if (!navigator.permissions?.query) {
      setGeolocationPermission("unknown");
      return undefined;
    }
    let active = true;
    let permissionStatus = null;
    const updatePermission = () => {
      if (active) {
        setGeolocationPermission(permissionStatus?.state || "unknown");
      }
    };
    void navigator.permissions.query({
      name: "geolocation"
    }).then(status => {
      if (!active) return;
      permissionStatus = status;
      updatePermission();
      if (permissionStatus.addEventListener) {
        permissionStatus.addEventListener("change", updatePermission);
      } else {
        permissionStatus.onchange = updatePermission;
      }
    }).catch(() => {
      if (active) setGeolocationPermission("unknown");
    });
    return () => {
      active = false;
      if (permissionStatus?.removeEventListener) {
        permissionStatus.removeEventListener("change", updatePermission);
      } else if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);
  const canShowOption = canOfferNearbyFirst(geolocationPermission);
  const isNearbyFirstActive = Boolean(showNearbyFirst) && Boolean(userLocation);
  useEffect(() => {
    if (!preferenceHydrated || restoredPreference.current) {
      return;
    }

    // A persisted preference is not a new user gesture. Start each page load
    // with nearby ordering off; enabling it below is the explicit gesture that
    // may read the device position or show a browser permission prompt.
    restoredPreference.current = true;
    setUserLocation(null);
    if (showNearbyFirst) void setShowNearbyFirst(false);
  }, [preferenceHydrated, setShowNearbyFirst, showNearbyFirst]);
  const requestNearbyFirst = useCallback(enabled => {
    if (!enabled) {
      setUserLocation(null);
      void setShowNearbyFirst(false);
      return;
    }
    if (!navigator.geolocation || geolocationPermission === "denied") {
      setUserLocation(null);
      void setShowNearbyFirst(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(({
      coords
    }) => {
      setUserLocation({
        latitude: coords.latitude,
        longitude: coords.longitude
      });
      void setShowNearbyFirst(true);
    }, error => {
      setUserLocation(null);
      if (error.code === 1) {
        setGeolocationPermission("denied");
      }
      void setShowNearbyFirst(false);
    }, {
      maximumAge: 0,
      timeout: 10_000
    });
  }, [geolocationPermission, setShowNearbyFirst]);
  return {
    canShowOption,
    geolocationPermission,
    isNearbyFirstActive,
    requestNearbyFirst,
    showNearbyFirst: Boolean(showNearbyFirst),
    userLocation
  };
}